import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { t } from '../i18n';
import { useDocuments } from '../lib/queries';
import { fmtMoney } from '../lib/format';
import { Checkbox, Empty, ErrorState, Input, Pagination, Select, useDebounced } from '../components/ui';
import { DocTable, type DocColumn } from '../components/DocTable';
import { UploadButton } from '../components/UploadDialog';
import { QuoteImportButton } from '../components/QuoteImport';
import { IconSearch, IconUpload } from '../components/icons';

const GROUPS: Array<{ key: string; label: string; types: string }> = [
  { key: '', label: 'כל המסמכים', types: '' },
  { key: 'invoices', label: 'חשבוניות', types: 'tax_invoice,tax_invoice_receipt' },
  { key: 'receipts', label: 'קבלות', types: 'receipt' },
  { key: 'credits', label: 'זיכויים', types: 'credit_note' },
  { key: 'quote', label: 'הצעות מחיר', types: 'quote' },
  { key: 'delivery_note', label: 'תעודות משלוח', types: 'delivery_note' },
];

type SortKey = 'issueDate' | 'number' | 'total' | 'dueDate' | 'importedAt';

export function DocumentsPage() {
  const [params, setParams] = useSearchParams();
  // Accept both ?group= and legacy ?types= links (e.g. from the dashboard).
  const typesParam = params.get('types') ?? '';
  const group = params.get('group') ?? GROUPS.find((g) => g.types === typesParam)?.key ?? (typesParam === 'quote' ? 'quote' : '');
  const types = GROUPS.find((g) => g.key === group)?.types ?? typesParam;
  const [q, setQ] = useState(params.get('q') ?? '');
  const dq = useDebounced(q, 250);
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const payment = params.get('payment') ?? '';
  const unbilled = params.get('unbilled') === '1';
  const sort = (params.get('sort') as SortKey) || 'issueDate';
  const dir = (params.get('dir') as 'asc' | 'desc') || 'desc';
  const page = Number(params.get('page')) || 1;

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v ? next.set(k, v) : next.delete(k));
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };
  useEffect(() => {
    if ((params.get('q') ?? '') !== dq) update({ q: dq || null });
  }, [dq]);

  const { data, isLoading, error, refetch } = useDocuments({
    types: types || undefined,
    q: dq || undefined,
    from: from || undefined,
    to: to || undefined,
    payment: payment || undefined,
    unbilled: unbilled || undefined,
    sort,
    dir,
    page,
    pageSize: 50,
  });

  const columns: DocColumn[] =
    group === 'quote'
      ? ['number', 'date', 'customer', 'description', 'total', 'quoteStatus', 'source']
      : group === 'delivery_note'
        ? ['number', 'date', 'customer', 'address', 'total', 'margin', 'billed', 'source']
        : group === 'invoices'
          ? ['type', 'number', 'date', 'customer', 'total', 'margin', 'payment', 'due', 'open']
          : ['type', 'number', 'date', 'customer', 'total', 'margin', 'payment', 'source', 'imported'];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.docs.title}</h1>
          <div className="sub">
            {data ? (
              <>
                {data.total} {t.common.results}
                {data.sumTotal ? <> · סה״כ <span className="tabular">{fmtMoney(data.sumTotal)}</span></> : null}
              </>
            ) : (
              ' '
            )}
          </div>
        </div>
        <div className="row wrap">
          <QuoteImportButton>
            <IconUpload /> הצעת מחיר מ-Excel
          </QuoteImportButton>
          <UploadButton>
            <IconUpload /> {t.profile.upload}
          </UploadButton>
        </div>
      </div>

      <div className="tabs">
        {GROUPS.map((g) => (
          <button key={g.key} className={`tab ${group === g.key ? 'active' : ''}`} onClick={() => update({ group: g.key || null, types: null, payment: null, unbilled: null })}>
            {g.label}
          </button>
        ))}
      </div>

      <div className="panel">
        <div className="panel-head row wrap" style={{ gap: 8 }}>
          <div className="search-input" style={{ flex: '1 1 220px', maxWidth: 320 }}>
            <IconSearch />
            <Input placeholder="מספר מסמך, לקוח, תיאור…" value={q} onChange={(e) => setQ(e.target.value)} aria-label={t.common.search} />
          </div>
          <label className="row small muted">
            מתאריך
            <Input type="date" value={from} onChange={(e) => update({ from: e.target.value || null })} style={{ width: 150 }} />
          </label>
          <label className="row small muted">
            עד
            <Input type="date" value={to} onChange={(e) => update({ to: e.target.value || null })} style={{ width: 150 }} />
          </label>
          {group === 'invoices' || group === '' ? (
            <Select style={{ width: 150 }} value={payment} onChange={(e) => update({ payment: e.target.value || null })} aria-label={t.docs.cols.payment}>
              <option value="">תשלום: הכל</option>
              <option value="open">פתוחות</option>
              <option value="overdue">באיחור</option>
              <option value="paid">שולמו</option>
              <option value="cancelled">בוטלו בזיכוי</option>
            </Select>
          ) : null}
          {group === 'delivery_note' ? <Checkbox checked={unbilled} onChange={(v) => update({ unbilled: v ? '1' : null })}>{t.docs.unbilledOnly}</Checkbox> : null}
        </div>
        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <>
            <DocTable
              docs={data?.items}
              loading={isLoading}
              columns={columns}
              sort={sort}
              dir={dir}
              onSort={(k) => update({ sort: k, dir: sort === k && dir === 'desc' ? 'asc' : 'desc' })}
              empty={<Empty title={t.docs.empty.title} body={t.docs.empty.body} />}
            />
            {data && data.total > data.pageSize ? <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={(p) => update({ page: String(p) })} /> : null}
          </>
        )}
      </div>
    </>
  );
}
